import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import { extras } from "../i18n";
import { API, bikeIsRideable } from "../config";
import { Gauge, IdCard, Users, Cog, Wrench, Clock, Sparkles, Package, Hand, KeyRound } from "lucide-react";

const FILTERS = ["all", "c125", "a2", "scooter", "trail", "naked", "neo-retro", "supermotard", "mini-naked"];

export const bikeImg = (b) => (b.image_path ? `${API}/bikes/image/${b.slug}` : b.image);

export default function Fleet({ bikes, onBook }) {
  const { t, lang } = useLang();
  const x = extras[lang] || extras.es;
  const [filter, setFilter] = useState("all");
  const filtered = bikes.filter((b) => {
    if (filter === "all") return true;
    if (filter === "c125") return b.cc <= 125;
    if (filter === "a2") return b.license !== "A";
    return b.category === filter;
  });
  return (
    <section id="fleet" className="max-w-7xl mx-auto px-6 py-24">
      <p className="eyebrow mb-4">{t.fleet.eyebrow}</p>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-topacio-ink">{t.fleet.title}</h2>
          <p className="mt-3 text-topacio-faded max-w-xl">{t.fleet.sub}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink transition-[transform,box-shadow,background-color] ${filter === f ? "bg-brand text-topacio-ink shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_#111]"}`}>
              {t.fleet.filters[f]}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((bike, i) => {
          const unavailable = !bikeIsRideable(bike);
          const statusLabel = x.notAvailable;
          const comingSoon = !!bike.coming_soon;
          return (
            <motion.div key={bike.slug} data-testid={`bike-card-${bike.slug}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className={`card-brutal overflow-hidden flex flex-col transition-[transform,box-shadow] ${unavailable ? "opacity-60" : "hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#111]"}`}>
              <div className="bg-white border-b-2 border-topacio-ink aspect-[4/3] overflow-hidden relative">
                <img src={bikeImg(bike)} alt={bike.name} loading="lazy" onError={(e) => { e.currentTarget.src = "/logo-topacio.jpg"; }}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                {comingSoon ? (
                  <span data-testid={`coming-soon-badge-${bike.slug}`} className="absolute top-3 left-3 badge-brutal bg-topacio-ink text-brand shadow-[2px_2px_0_0_#111]">
                    <Clock className="w-3 h-3" /> {x.comingSoon}
                  </span>
                ) : unavailable ? (
                  <span data-testid={`workshop-badge-${bike.slug}`} className="absolute top-3 left-3 badge-brutal bg-topacio-red text-white shadow-[2px_2px_0_0_#111]">
                    <Wrench className="w-3 h-3" /> {statusLabel}
                  </span>
                ) : bike.is_new ? (
                  <span data-testid={`new-badge-${bike.slug}`} className="absolute top-3 left-3 badge-brutal bg-topacio-green text-white shadow-[2px_2px_0_0_#111]">
                    <Sparkles className="w-3 h-3" /> {x.newBadge}
                  </span>
                ) : null}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-base font-bold text-topacio-ink leading-snug">{bike.name}</h3>
                  <span className="shrink-0 badge-brutal bg-brand text-topacio-ink">{bike.group}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs font-semibold text-topacio-faded">
                  <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-topacio-red" />{bike.cc}cc</span>
                  <span className="flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5 text-topacio-red" />{bike.license}</span>
                  <span className="flex items-center gap-1.5"><Cog className="w-3.5 h-3.5 text-topacio-red" />{bike.automatic ? t.fleet.auto : t.fleet.manual}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-topacio-red" />{t.fleet.seats}</span>
                  {bike.topcase_included && (
                    <span data-testid={`topcase-included-${bike.slug}`} className="flex items-center gap-1.5 text-topacio-green font-extrabold"><Package className="w-3.5 h-3.5" />{x.topcaseIncluded}</span>
                  )}
                  {bike.keyless_included && (
                    <span data-testid={`keyless-included-${bike.slug}`} className="flex items-center gap-1.5 text-topacio-green font-extrabold"><KeyRound className="w-3.5 h-3.5" />{x.keylessIncluded}</span>
                  )}
                  <span data-testid={`gloves-included-${bike.slug}`} className="flex items-center gap-1.5 text-topacio-green font-extrabold"><Hand className="w-3.5 h-3.5" />{x.glovesIncluded}</span>
                </div>
                <p className="mt-2 text-[10px] text-topacio-faded font-semibold italic">{x.modelOrSimilar}</p>
                <div className="mt-6 pt-5 border-t-2 border-dashed border-zinc-200 flex items-end justify-between mt-auto">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-topacio-faded font-bold">{t.fleet.from}</div>
                    <div className="font-display font-black text-2xl text-topacio-ink">
                      {bike.price_1d}€<span className="text-xs text-topacio-faded font-body font-semibold">{t.fleet.day}</span>
                    </div>
                  </div>
                  {comingSoon ? (
                    <span data-testid={`coming-soon-btn-${bike.slug}`} className="px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink bg-topacio-ink text-brand">
                      {x.comingSoon}
                    </span>
                  ) : unavailable ? (
                    <span data-testid={`book-disabled-${bike.slug}`} className="px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-zinc-300 text-zinc-400 cursor-not-allowed">
                      {x.notAvailable}
                    </span>
                  ) : (
                    <button data-testid={`book-${bike.slug}`} onClick={() => onBook(bike)}
                      className="btn-primary !px-5 !py-2.5 !text-xs">
                      {t.fleet.book}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
